<template>
  <tr v-if="loading">
    <td
      v-for="num in tableColumnSize"
      :key="num"
      class="border-x-1 border-color-divider"
    >
      <Skeleton width="100%" height="1rem" />
    </td>
  </tr>

  <tr v-else>
    <td>
      <router-link
        v-tooltip.left="{
          value: includedShipmentLineItemsTooltip,
          disabled: !shipmentRateAllocation,
        }"
        :to="{
          name: 'shipmentDetail',
          params: {
            shipmentId: shipmentId,
          },
        }"
        class="font-medium"
      >
        {{
          t('shipments.shipmentNumParam', {
            param: shipmentId,
          })
        }}
      </router-link>
    </td>

    <td class="border-x-1 border-color-divider text-right">
      <span v-if="shipmentRateAllocation?.isCostEstimateInvoiceTypes">
        {{ renderMoney(allocatedCostForOrder) }}
      </span>
      <span v-else>
        {{ '-' }}
      </span>
    </td>

    <td class="border-x-1 border-color-divider text-right">
      <span v-if="!shipmentRateAllocation?.isCostEstimateInvoiceTypes">
        {{ renderMoney(allocatedCostForOrder) }}
      </span>
      <span v-else>
        {{ '-' }}
      </span>
    </td>
  </tr>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Currency, Money } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { ShipmentRateAllocation } from '@/model/shipmentFinancials';

import Skeleton from 'primevue/skeleton';

const props = defineProps({
  shipmentId: {
    type: Number,
    required: true,
  },
  shipmentRateAllocation: {
    type: Object as PropType<ShipmentRateAllocation | undefined>,
    default: () => undefined,
  },
  tableColumnSize: {
    type: Number,
    required: true,
  },
  billingCurrency: {
    type: String as PropType<Currency>,
    default: () => Currency.USD,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});
const {
  shipmentId,
  shipmentRateAllocation,
  tableColumnSize,
  billingCurrency,
  loading,
} = toRefs(props);

const { t } = useI18n();
const vTooltip = useTooltip();

const includedShipmentLineItemsTooltip = computed(() => {
  const lineItems = shipmentRateAllocation?.value?.lineItems || [];

  const shipmentLineItems = lineItems.map(
    (lineItem) => lineItem.shipmentLineItem,
  );
  const uniqueShipmentLineItems = uniqWith(shipmentLineItems, isEqual);

  const descriptionAndQuantityString = ['Shipment includes'];
  uniqueShipmentLineItems.forEach((item) => {
    descriptionAndQuantityString.push(
      `- ${item?.description} : \n${item?.quantity} ${item?.handlingUnit?.name}`,
    );
  });

  return descriptionAndQuantityString.join('\n');
});

const allocatedCostForOrder = computed(() =>
  shipmentRateAllocation?.value?.lineItems
    .map((lineItem) => lineItem.amount)
    .filter(notNull)
    .reduce(
      (prev: Money, cur: Money) => prev.plus(cur),
      Money.zero(billingCurrency.value),
    ),
);
</script>
